import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import {
  Loader2, LogOut, Car, MapPin, CheckCircle2, Clock, Phone, Building2, MessageSquare, Bell, FileText, XCircle, ExternalLink,
} from "lucide-react";
import clientApi, { clientToken } from "@/lib/clientApi";
import { money, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StarInput, StarsDisplay } from "@/components/StarInput";
import { toast } from "sonner";

function RateMechanicForm({ reqId, onDone }) {
  const [s, setS] = useState({ quality: 0, communication: 0, punctuality: 0 });
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!s.quality || !s.communication || !s.punctuality) { toast.error("Please rate all categories"); return; }
    setSaving(true);
    try {
      await clientApi.post(`/clients/requests/${reqId}/rate-mechanic`, { ...s, comment });
      toast.success("Thanks for rating your mechanic!");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit rating"); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-4 pt-4 border-t border-slate-800" data-testid={`rate-mechanic-form-${reqId}`}>
      <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">Rate your mechanic</p>
      <div className="space-y-1.5">
        <StarInput label="Quality of work" value={s.quality} onChange={(v) => setS((p) => ({ ...p, quality: v }))} testid={`rm-quality-${reqId}`} />
        <StarInput label="Communication" value={s.communication} onChange={(v) => setS((p) => ({ ...p, communication: v }))} testid={`rm-communication-${reqId}`} />
        <StarInput label="Punctuality" value={s.punctuality} onChange={(v) => setS((p) => ({ ...p, punctuality: v }))} testid={`rm-punctuality-${reqId}`} />
      </div>
      <Textarea data-testid={`rm-comment-${reqId}`} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="How was the service? (optional)" className="mt-3 bg-slate-950 border-slate-700 text-sm" />
      <Button size="sm" data-testid={`rm-submit-${reqId}`} onClick={submit} disabled={saving} className="mt-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
        {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Submit rating
      </Button>
    </div>
  );
}

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState("");
  const [responding, setResponding] = useState("");

  const load = () => Promise.all([clientApi.get("/clients/me"), clientApi.get("/clients/me/requests")])
    .then(([m, r]) => { setMe(m.data.client); setReqs(r.data.requests || []); })
    .catch(() => { clientToken.clear(); navigate("/client/login"); })
    .finally(() => setLoading(false));

  useEffect(() => { if (clientToken.get()) load(); /* eslint-disable-next-line */ }, []);

  if (!clientToken.get()) return <Navigate to="/client/login" replace />;

  const logout = () => { clientToken.clear(); toast.success("Signed out"); navigate("/"); };

  const accept = async (reqId, bidId) => {
    setAccepting(bidId);
    try {
      await clientApi.post(`/clients/requests/${reqId}/bids/${bidId}/accept`);
      toast.success("Bid accepted — your mechanic has been notified");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept bid");
    } finally {
      setAccepting("");
    }
  };

  const respondEstimate = async (reqId, decision) => {
    setResponding(reqId + decision);
    try {
      await clientApi.post(`/clients/requests/${reqId}/estimate/respond`, { decision });
      toast.success(decision === "approved" ? "Estimate approved — your mechanic has been notified" : "Estimate declined");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit your response");
    } finally {
      setResponding("");
    }
  };

  const unseen = reqs.filter((r) => r.estimate_sent && !r.client_viewed_estimate).length;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      <header className="h-16 border-b border-slate-800/60 bg-slate-950/80 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-4xl mx-auto h-full px-6 flex items-center gap-3">
          <img src="/pitcrew-logo-horizontal.png" alt="Pitcrew" className="h-8 object-contain" data-testid="client-dashboard-logo" />
          <div className="ml-auto flex items-center gap-2">
            <div className="relative" data-testid="client-notif-bell">
              <Bell className="h-5 w-5 text-slate-400" />
              {unseen > 0 && (
                <span data-testid="client-notif-count" className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 rounded-full bg-amber-500 text-slate-950 text-[10px] font-bold flex items-center justify-center">{unseen}</span>
              )}
            </div>
            <Button variant="ghost" data-testid="client-new-request" onClick={() => navigate("/")} className="text-slate-300 hover:text-white hover:bg-slate-800">New request</Button>
            <Button variant="ghost" size="icon" data-testid="client-logout" onClick={logout} className="text-slate-500 hover:text-rose-400 hover:bg-slate-800"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="font-head font-black text-3xl text-slate-50">My Repairs</h1>
        <p className="text-slate-400 mt-1">{me ? `Signed in as ${me.email}` : ""}</p>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
        ) : reqs.length === 0 ? (
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-16 text-center mt-8">
            <Car className="h-10 w-10 text-slate-700 mx-auto mb-3" />
            <p className="text-slate-400 mb-4">No requests yet. Requests you submit with this email will appear here.</p>
            <Button onClick={() => navigate("/")} data-testid="client-empty-request" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">Request an estimate</Button>
          </div>
        ) : (
          <div className="space-y-4 mt-8">
            {reqs.map((r) => {
              const vehicle = [r.vehicle_year, r.vehicle_make, r.vehicle_model].filter(Boolean).join(" ");
              const accepted = r.status === "accepted";
              const completed = r.status === "completed" || r.job_completed;
              const booked = accepted || completed;
              const bids = r.bids || [];
              return (
                <div key={r.id} data-testid={`client-request-${r.id}`} className="rounded-xl border border-slate-800 bg-slate-900 p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-100 flex items-center gap-2"><Car className="h-4 w-4 text-slate-500" /> {vehicle || "Vehicle"}</p>
                      <p className="text-sm text-slate-400 mt-1 flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-slate-600" /> {r.place_name || ""} {r.zip_code}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 ${completed ? "text-blue-400 bg-blue-400/10 border border-blue-400/20" : accepted ? "text-emerald-400 bg-emerald-400/10 border border-emerald-400/20" : "text-amber-400 bg-amber-400/10 border border-amber-400/20"}`}>
                      {completed ? <CheckCircle2 className="h-3 w-3" /> : accepted ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}{completed ? "Completed" : accepted ? "Mechanic booked" : `${bids.length} estimate${bids.length === 1 ? "" : "s"}`}
                    </span>
                  </div>
                  {r.issue && <p className="text-sm text-slate-400 mt-3">{r.issue}</p>}

                  {r.estimate_sent && (
                    <div data-testid={`estimate-ready-${r.id}`} className={`mt-4 rounded-lg border p-3 ${r.estimate_status === "approved" ? "border-emerald-500/40 bg-emerald-500/10" : r.estimate_status === "declined" ? "border-slate-800 bg-slate-950/40" : !r.client_viewed_estimate ? "border-amber-500/40 bg-amber-500/10" : "border-slate-800 bg-slate-950/40"}`}>
                      <div className="flex items-start gap-3">
                        <FileText className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-slate-100 flex items-center gap-2">
                            Your mechanic sent a formal {r.estimate_doc_type || "estimate"}
                            {(!r.client_viewed_estimate && (!r.estimate_status || r.estimate_status === "pending")) && <span data-testid={`estimate-new-${r.id}`} className="text-[10px] uppercase font-bold bg-amber-500 text-slate-950 rounded-full px-1.5 py-0.5">New</span>}
                          </p>
                          <p className="text-sm text-slate-300 mt-0.5">Total: <span className="font-bold text-amber-400">{r.estimate_total != null ? money(r.estimate_total) : "—"}</span></p>
                          {r.estimate_summary && <p className="text-xs text-slate-500 mt-1">{r.estimate_summary}</p>}
                        </div>
                      </div>
                      {r.estimate_status === "approved" ? (
                        <p data-testid={`estimate-approved-${r.id}`} className="mt-2 text-sm font-semibold text-emerald-400 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> You approved this estimate</p>
                      ) : r.estimate_status === "declined" ? (
                        <p data-testid={`estimate-declined-${r.id}`} className="mt-2 text-sm text-slate-400 flex items-center gap-2"><XCircle className="h-4 w-4" /> You declined this estimate</p>
                      ) : (
                        <div className="mt-3 flex gap-2">
                          <Button size="sm" data-testid={`estimate-approve-${r.id}`} onClick={() => respondEstimate(r.id, "approved")} disabled={responding === r.id + "approved"}
                            className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
                            {responding === r.id + "approved" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Approve
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`estimate-decline-${r.id}`} onClick={() => respondEstimate(r.id, "declined")} disabled={responding === r.id + "declined"}
                            className="bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800">
                            {responding === r.id + "declined" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <XCircle className="h-4 w-4 mr-1.5" />} Decline
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {bids.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-800 space-y-3" data-testid={`client-bids-${r.id}`}>
                      <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                        {booked ? "Estimates received" : "Compare estimates"}
                      </p>
                      {bids.map((b) => (
                        <div key={b.id} data-testid={`client-bid-${b.id}`} className={`rounded-lg border p-3 ${b.status === "accepted" ? "border-emerald-500/30 bg-emerald-500/5" : b.status === "declined" ? "border-slate-800 bg-slate-950/40 opacity-60" : "border-slate-800 bg-slate-950/40"}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-medium text-slate-100 flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-slate-500" /> {b.business_name || b.mechanic_name || "Mechanic"}</p>
                              <div className="mt-1 flex items-center gap-2">
                                {b.rating?.avg ? (
                                  <span data-testid="mechanic-rating"><StarsDisplay value={b.rating.avg} showNumber count={b.rating.count} /></span>
                                ) : (
                                  <span className="text-[11px] text-slate-500" data-testid="mechanic-no-reviews">No reviews yet</span>
                                )}
                                <button data-testid={`view-profile-${b.id}`} onClick={() => navigate(`/mechanic/${encodeURIComponent(b.mechanic_email)}`)}
                                  className="text-[11px] text-amber-400 hover:text-amber-300 inline-flex items-center gap-0.5">
                                  Profile <ExternalLink className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                            <span className="font-bold text-amber-400 shrink-0">{b.amount != null ? money(b.amount) : "—"}</span>
                          </div>
                          {b.message && <p className="text-sm text-slate-400 mt-2 flex items-start gap-2"><MessageSquare className="h-3.5 w-3.5 text-slate-600 mt-0.5" /> {b.message}</p>}
                          {b.status === "accepted" ? (
                            <p className="text-sm text-emerald-400 mt-2 flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {b.phone || "Contact shared"}</p>
                          ) : b.status === "declined" ? (
                            <p className="text-xs text-slate-500 mt-2">Not selected</p>
                          ) : booked ? null : (
                            <Button size="sm" data-testid={`accept-bid-${b.id}`} onClick={() => accept(r.id, b.id)} disabled={accepting === b.id}
                              className="mt-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
                              {accepting === b.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Accept & share my contact
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {r.job_completed && (
                    r.client_rated_mechanic ? (
                      <p data-testid={`mechanic-rated-${r.id}`} className="mt-4 pt-4 border-t border-slate-800 text-sm text-emerald-400 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> You rated your mechanic — thanks!</p>
                    ) : (
                      <RateMechanicForm reqId={r.id} onDone={load} />
                    )
                  )}

                  <p className="text-[11px] text-slate-600 mt-3">Submitted {fmtDate(r.created_at)}</p>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
